import { useEffect } from 'react';
import useSWR from 'swr';
import { formatEther } from 'viem';
import { ContainerShape, InnerShape, InnerShape2 } from './components/Shape';
import { icons } from './assets/icons/icons';
import { GRAPHQL_URL, ZERO_ADDRESS } from '~/core/constants';
import { RESOLVER_ADDRESS } from './constants';
import Loader from '~/core/components/Loader';

// --- Types ---
interface SubgraphGame {
  id: string;
  player: { id: string };
  betAmount: string;
  payoutAmount: string;
  createdAt: string;
  resolvedAt: string;
}

interface RecentWin {
  id: string;
  player: string;
  bet: bigint;
  payout: bigint;
  multiplier: number;
  createdAt: number;
  time: string;
}

type ResolverBalanceResult = {
  balance: string;
  totalDeposited: string;
  totalWithdrawn: string;
  totalGamesResolved: string;
  totalWins: string;
  totalLosses: string;
  totalBetAmountReceived: string;
  totalPayoutAmountSent: string;
}

const fetchResolverBalance = async (): Promise<ResolverBalanceResult> => {
  // ID is resolver address + token address (concatenated, lowercase)
  const resolverBalanceId = `${RESOLVER_ADDRESS.toLowerCase()}${ZERO_ADDRESS.slice(2).toLowerCase()}`

  const response = await fetch(GRAPHQL_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      query: `
        query {
          resolverBalance(id: "${resolverBalanceId}") {
            balance
            totalDeposited
            totalWithdrawn
            totalGamesResolved
            totalWins
            totalLosses
            totalBetAmountReceived
            totalPayoutAmountSent
          }
        }
      `
    }),
  });

  const { data } = await response.json();

  return data?.resolverBalance ?? {
    balance: '0',
    totalDeposited: '0',
    totalWithdrawn: '0',
    totalGamesResolved: '0',
    totalWins: '0',
    totalLosses: '0',
    totalBetAmountReceived: '0',
    totalPayoutAmountSent: '0',
  }
};

const useResolverBalance = () => {
  return useSWR('blinko-resolver-balance', fetchResolverBalance, {
    refreshInterval: 10000,
  });
};


const fetchGames = async (first: number): Promise<RecentWin[]> => {
  try {
    const response = await fetch(GRAPHQL_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        query: `
          query {
            games(
              where: { status: Won }
              orderBy: resolvedAt
              orderDirection: desc
              first: ${first}
            ) {
              id
              player { id }
              betAmount
              payoutAmount
              createdAt
              resolvedAt
            }
          }
        `
      }),
    });

    const { data } = await response.json();
    return (data.games || []).map((game: SubgraphGame) => {
      const bet = BigInt(game.betAmount);
      const payout = BigInt(game.payoutAmount);
      const multiplier = bet > 0n 
        ? Number((payout * 10000n) / bet) / 10000
        : 0;

      // Display (and sort by) the game *creation* time, matching the History page
      // which renders `createdAt`. The query fetches the most-recently *resolved*
      // wins; we re-sort by `createdAt` in the component so the time column reads
      // in order even when a win's reveal/cashout was delayed (common for High Wins).
      const createdAt = Number(game.createdAt);
      const date = new Date(createdAt * 1000);
      const timeStr = date.toLocaleTimeString('en-US', {
        hour12: false, 
        hour: '2-digit', 
        minute: '2-digit',
        second: '2-digit'
      });

      return {
        id: game.id,
        player: game.player.id,
        bet,
        payout,
        multiplier,
        createdAt,
        time: timeStr,
      };
    });
  } catch (e) {
    console.error('Failed to fetch games:', e);
    return [];
  }
};

const fetchWins = () => fetchGames(200);

const useWins = () => {
    return useSWR('blinko-all-wins', fetchWins, {
        refreshInterval: 10000,
    });
}

// --- Components ---

const StatCard = ({ title, value, subValue, icon, colorClass = "text-white" }: { title: string, value: string | number | React.ReactNode, subValue?: string, icon: string, colorClass?: string }) => {
  return (
    <InnerShape2 cornerDiagonal={10} cornerRadius={8} strokeWidth={1}>
      <div className="flex flex-col gap-2 p-3 h-full justify-between">
        <div className="flex items-center gap-2">
          <InnerShape cornerDiagonal={6} cornerRadius={4} strokeWidth={1}>
            <div className='p-1.5'>
              <img src={icon} className="w-3.5 h-3.5" alt={title} />
            </div>
          </InnerShape>
          <p className="text-[10px] uppercase font-blinko text-white/50 tracking-wider">{title}</p>
        </div>
        <div>
          <div className={`text-xl md:text-2xl font-black ${colorClass} truncate`}>
            {value}
          </div>
          {subValue && <p className="text-xs font-bold text-white/50">{subValue}</p>}
        </div>
      </div>
    </InnerShape2>
  );
};

const RecentWinsList = ({ wins }: { wins: RecentWin[] }) => {
    return (
        <div className="overflow-x-auto">
            <table className="w-full text-left text-xs text-white/70">
                <thead className="uppercase text-white/50 text-[10px]">
                    <tr>
                        <th className="px-2 py-2 rounded-tl-lg w-16">Time</th>
                        <th className="px-2 py-2">Player</th>
                        <th className="px-2 py-2 text-right">Bet</th>
                        <th className="px-2 py-2 text-right">Win</th>
                        <th className="px-2 py-2 text-right rounded-tr-lg">Mult</th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-white/10">
                    {wins.map((win) => (
                        <tr key={win.id} className="hover:bg-white/5 transition-colors">
                            <td className="px-2 py-1.5 whitespace-nowrap font-mono text-[10px] text-white/50">{win.time}</td>
                            <td className="px-2 py-1.5 font-mono text-[10px] text-blue-300">
                                <a href={`/history/${win.player}`} target="_blank" rel="noreferrer" className="hover:underline">
                                    {win.player.slice(0, 4)}...{win.player.slice(-4)}
                                </a>
                            </td>
                            <td className="px-2 py-1.5 text-right font-mono text-[10px]">{formatEther(win.bet)}</td>
                            <td className="px-2 py-1.5 text-right font-mono text-green-400 font-bold text-[10px]">
                                {formatEther(win.payout)}
                            </td>
                            <td className="px-2 py-1.5 text-right">
                                <a href={`/replay/${win.id}`} target="_blank" rel="noreferrer" className="inline-block transition-opacity hover:opacity-80">
                                    <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${
                                        win.multiplier >= 10 ? 'bg-purple-500/20 text-purple-300 border border-purple-500/50' :
                                        win.multiplier >= 2 ? 'bg-green-500/20 text-green-300' :
                                        'bg-white/10 text-white/50'
                                    }`}>
                                        {win.multiplier.toFixed(2)}x
                                    </span>
                                </a>
                            </td>
                        </tr>
                    ))}
                    {wins.length === 0 && (
                        <tr>
                            <td colSpan={5} className="px-4 py-8 text-center text-white/30 italic text-xs">
                                No wins found.
                            </td>
                        </tr>
                    )}
                </tbody>
            </table>
        </div>
    );
};

export const PnLDashboard = () => {
  const { data: resolverBalance, isLoading } = useResolverBalance();
  const { data: allWins } = useWins();

  const balance = BigInt(resolverBalance?.balance || '0');
  const totalBet = BigInt(resolverBalance?.totalBetAmountReceived || '0');
  const totalPayout = BigInt(resolverBalance?.totalPayoutAmountSent || '0');
  const totalGames = Number(resolverBalance?.totalGamesResolved || 0);
  const housePnL = totalBet - totalPayout;
  const rtp = totalBet > 0n
    ? Number((totalPayout * 10000n) / totalBet) / 100
    : 0;
  
  // Re-sort by createdAt desc so the displayed time column is monotonic
  // (the query returns resolvedAt desc, which is out of order for delayed reveals).
  const sortedWins = allWins
    ? [...allWins].sort((a, b) => b.createdAt - a.createdAt)
    : [];
  const recentWins = sortedWins.slice(0, 20);
  const highWins = sortedWins.filter(w => w.multiplier >= 10);

  const formatEth = (val: bigint) => {
    const formatted = formatEther(val);
    const [int, dec] = formatted.split('.');
    if (!dec) return int;
    return `${int}.${dec.slice(0, 4)}`;
  };

  useEffect(() => {
    document.title = `${formatEth(balance)} ETH`;
  }, [balance]);

  return (
    <div className="min-h-screen bg-black text-white overflow-y-auto">
      <div className="relative z-10 w-full max-w-5xl mx-auto px-2 py-4">

        {isLoading ? (
          <div className="flex justify-center items-center h-64">
            <Loader size="48px" stroke="#ffffff" />
          </div>
        ) : resolverBalance ? (
            <ContainerShape
                cornerDiagonal={20}
                cornerRadius={12}
                strokeWidth={1}
            >
                <div className="p-3 md:p-6 backdrop-blur-sm">
                    {/* Stats Grid - 2 cols on mobile, 3 on tablet, 6 on large */}
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                        <StatCard
                            title="Balance"
                            value={
                                <span>
                                    {formatEth(balance)} <span className="text-sm text-white/50">ETH</span>
                                </span>
                            }
                            icon={icons.coins}
                            colorClass={balance >= 0n ? 'text-green-400' : 'text-red-400'}
                        />

                        <StatCard
                          title="Cumulative PnL"
                          value={
                              <span>
                                  {formatEth(housePnL)} <span className="text-sm text-white/50">ETH</span>
                              </span>
                          }
                          icon={icons.coins}
                          colorClass={housePnL >= 0n ? 'text-green-400' : 'text-red-400'}
                        />


                        {/* RTP */}
                        <StatCard
                            title="RTP"
                            value={`${rtp.toFixed(2)}%`}
                            icon={icons.history}
                            colorClass={rtp < 99 ? 'text-green-400' : 'text-yellow-400'}
                        />

                        {/* Total Games */}
                        <StatCard
                            title="Total Games"
                            value={totalGames.toLocaleString()}
                            icon={icons.rocket}
                        />

                        {/* Total Bet */}
                        <StatCard
                            title="Total Bet"
                            value={
                                <span>
                                    {formatEth(totalBet)} <span className="text-sm text-white/50">ETH</span>
                                </span>
                            }
                            icon={icons.currencyEthereum}
                        />

                         {/* Total Win */}
                         <StatCard
                            title="Total Win"
                            value={
                                <span>
                                    {formatEth(totalPayout)} <span className="text-sm text-white/50">ETH</span>
                                </span>
                            }
                            icon={icons.cup}
                        />
                    </div>

                    <div className="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-4">
                         {/* Recent Wins */}
                         <div className="flex flex-col gap-2">
                             <div className='flex items-center gap-2 px-1'>
                                <p className="text-xs font-blinko text-white/70 uppercase tracking-widest">Recent Wins</p>
                            </div>
                            <InnerShape2 cornerDiagonal={10} cornerRadius={8} strokeWidth={1}>
                                <div className='p-3'>
                                    <RecentWinsList wins={recentWins || []} />
                                </div>
                            </InnerShape2>
                         </div>

                        {/* High Wins Accumulator */}
                        <div className="flex flex-col gap-2">
                            <div className='flex items-center justify-between px-1'>
                                <p className="text-xs font-blinko text-white/70 uppercase tracking-widest">High Wins</p>
                                <span className="text-[10px] bg-purple-500/20 text-purple-300 px-1.5 py-0.5 rounded border border-purple-500/50">≥ 10x</span>
                            </div>
                            <InnerShape2 cornerDiagonal={10} cornerRadius={8} strokeWidth={1}>
                                <div className='p-3 min-h-[400px] max-h-[600px] overflow-y-auto custom-scrollbar outline-none'>
                                    {highWins.length === 0 ? (
                                        <div className="flex flex-col items-center justify-center h-full text-white/20 italic text-xs py-8">
                                            <p>Waiting for big wins...</p>
                                        </div>
                                    ) : (
                                        <RecentWinsList wins={highWins} />
                                    )}
                                </div>
                            </InnerShape2>
                        </div>
                    </div>
                </div>
            </ContainerShape>
        ) : (
             <div className="flex justify-center items-center h-64">
                <p className="text-red-400">Failed to load data.</p>
             </div>
        )}
      </div>
    </div>
  );
};
